import { useEffect, useState, useRef, useCallback } from 'react';
import API_ENDPOINTS from '../Config/api';
import { fetchWithAuth } from '../Utils/fetchWithAuth';

function getAuthHeaders() {
  const token = sessionStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export default function useNotifications({pollInterval = 10000} = {}) {
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const intervalRef = useRef(null);

  const fetchUnread = useCallback(async () => {
    try {
      const res = await fetchWithAuth(API_ENDPOINTS.notificationsUnreadCount, { method: 'GET' });
      if (!res) return;
      if (!res.ok) return;
      const j = await res.json();
      setUnreadCount(j.count || 0);
    } catch (e) {
      // ignore network errors for polling
    }
  }, []);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchWithAuth(API_ENDPOINTS.notifications, { method: 'GET' });
      if (!res) return;
      if (!res.ok) return;
      const j = await res.json();
      // Ensure client-side ordering: prefer incident number (from url or title) descending,
      // fallback to created_at timestamp, then id.
      const arr = j || [];
      function extractIncident(n){
        if (!n) return null;
        const url = n.url || '';
        const m = url.match(/[?&]incident=(\d+)/);
        if (m) return Number(m[1]);
        const title = (n.title || '');
        const mt = title.match(/(\d+)$/);
        if (mt) return Number(mt[1]);
        return null;
      }
      if (Array.isArray(arr) && arr.length > 0) {
        arr.sort((a,b) => {
          const ia = extractIncident(a);
          const ib = extractIncident(b);
          if (ia !== null && ib !== null) return ib - ia;
          if (ia !== null) return -1;
          if (ib !== null) return 1;
          const ta = a.created_at ? new Date(a.created_at).getTime() : 0;
          const tb = b.created_at ? new Date(b.created_at).getTime() : 0;
          if (ta !== tb) return tb - ta;
          return Number(b.id) - Number(a.id);
        });
      }
      setNotifications(arr);
    } catch (e) {
      console.error('Notifications fetch failed', e);
    } finally {
      setLoading(false);
    }
  }, []);

  const markRead = useCallback(async (ids=[]) => {
    try {
      const res = await fetchWithAuth(API_ENDPOINTS.notificationsMarkRead, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids })
      });
      if (!res) throw new Error('mark failed');
      if (!res.ok) throw new Error('mark failed');
      // refresh
      await fetchUnread();
      await fetchList();
    } catch (e) {
      console.error('Mark read failed', e);
    }
  }, [fetchList, fetchUnread]);

  useEffect(() => {
    // initial
    fetchUnread();
    // start polling
    intervalRef.current = setInterval(fetchUnread, pollInterval);
    return () => clearInterval(intervalRef.current);
  }, [fetchUnread, pollInterval]);

  return {
    unreadCount,
    notifications,
    loading,
    fetchList,
    markRead,
    refreshUnread: fetchUnread,
  };
}
